import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { Table } from '../../shared/table/table';
import { Popup } from '../../../core/shared/components/popup/popup';
import { AppIcons } from '../../../core/icons/fa-icons';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { Setup } from '../../setup/shared/service/setup';
import { TableColumn, TableAction } from '../../shared/models/table-models';
import { BackButton } from '../../shared/buttons/back-button';
import { classifyQuestionType, QuestionTypeCategory } from '../question-type.util';

@Component({
  selector: 'app-answer-setup',
  imports: [CommonModule, Table, Popup, BackButton, TranslatePipe],
  templateUrl: './answer-setup.html',
  styleUrl: './answer-setup.css',
})
export class AnswerSetup implements OnInit {

  icons = AppIcons;

  list: any[] = [];
  columns: TableColumn[] = [];

  showPopup = false;
  isEditMode = false;
  formData: any = {};
  fields: DynamicFieldConfig[] = [];

  showDeletePopup = false;
  deleteMessage = '';
  selectedRow: any = null;

  qstSeq: number | null = null;

  // ================= QUESTION TYPE =================
  qstTypKey: number | null = null;
  qstTypLabel = '';
  typeCategory: QuestionTypeCategory = 'other';

  constructor(
    private setup: Setup,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService
  ) {}

  // ================= INIT =================
  ngOnInit(): void {
    combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(([params, queryParams]) => {
      const id = params.get('id');
      this.qstSeq = id ? +id : null;

      const typeKey = queryParams.get('type');
      this.qstTypKey = typeKey ? +typeKey : null;
      this.qstTypLabel = queryParams.get('typeName') || '';
      this.typeCategory = classifyQuestionType(this.qstTypLabel, this.qstTypKey);

      this.buildFields();
      this.buildColumns();
      this.loadData();
    });
  }

  // ================= ANSWER RULES (based on question type) =================
  get canAddAnswer(): boolean {
    if (this.typeCategory === 'radio') return false;
    if (this.typeCategory === 'input') return this.list.length < 1;
    return true;
  }

  // ================= FIELDS =================
  buildFields() {
    this.fields = [
      {
        key: 'ansSeq',
        label: 'QUESTIONNAIRE.ID',
        type: 'text',
        hidden: true,
      },
      {
        key: 'ansStr',
        label: 'QUESTIONNAIRE.ANSWER_SETUP.ANSWER',
        type: 'text',
        validations: { required: true },
        readonly: this.typeCategory === 'radio',
      },
      {
        key: 'ansScore',
        label: 'QUESTIONNAIRE.ANSWER_SETUP.SCORE',
        type: 'number',
        validations: { required: true, min: 0, max: 100 },
      },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'select',
        options$: [
          { label: 'COMMON.ACTIVE',   value: 706 },
          { label: 'COMMON.INACTIVE', value: 707 },
        ],
      }
    ];
  }

  // ================= TABLE =================
  buildColumns() {
    this.columns = [
      { key: 'ansSeq',   label: 'QUESTIONNAIRE.ID' },
      { key: 'ansStr',   label: 'QUESTIONNAIRE.ANSWER_SETUP.ANSWER' },
      { key: 'ansScore', label: 'QUESTIONNAIRE.ANSWER_SETUP.SCORE' },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'badge',
        map:      { 706: 'COMMON.ACTIVE', 707: 'COMMON.INACTIVE' },
        badgeMap: { 706: 'status-active', 707: 'status-inactive' }
      }
    ];
  }

  // ================= LOAD =================
  loadData() {
    if (!this.qstSeq) {
      this.list = [];
      return;
    }

    this.setup.getAnswerList(this.qstSeq).subscribe({
      next: (res: any[]) => {
        this.list = (res || []).map(item => ({
          ansSeq:   item.answrSeq,
          ansStr:   item.answrStr,
          ansScore: item.answrScore,
          status:   Number(item.answrStsKey),
        }));

        if (this.typeCategory === 'radio' && this.list.length === 0) {
          this.createRadioYesNoAnswers();
        }
      },
      error: () => this.list = []
    });
  }

  // ================= RADIO (Yes/No) =================
  private createRadioYesNoAnswers(): void {
    const basePayload = {
      answrSeq: null,
      qstSeq: this.qstSeq,
      answrScore: 0,
      answrStsKey: 706,
      delFlg: false,
    };

    this.setup.saveAnswer({ ...basePayload, answrStr: 'Yes' }).subscribe({
      next: () => {
        this.setup.saveAnswer({ ...basePayload, answrStr: 'No' }).subscribe({
          next: () => this.loadData(),
          error: () => this.toastr.error(this.translate.instant('QUESTIONNAIRE.ANSWER_SETUP.DEFAULT_ANSWERS_FAILED')),
        });
      },
      error: () => this.toastr.error(this.translate.instant('QUESTIONNAIRE.ANSWER_SETUP.DEFAULT_ANSWERS_FAILED')),
    });
  }

  // ================= POPUP =================
  openPopup(data?: any) {
    this.showPopup = true;

    if (data) {
      // ✅ EDIT MODE — pass existing seq
      this.isEditMode = true;
      this.formData = {
        ansSeq:   data.ansSeq,      // ✅ existing seq — NOT null
        ansStr:   data.ansStr,
        ansScore: data.ansScore,
        status:   Number(data.status),
      };
    } else {
      // ✅ ADD MODE — seq is null
      this.isEditMode = false;
      this.formData = {
        ansSeq:   null,             // ✅ null for new record
        ansStr:   '',
        ansScore: null,
        status:   706
      };
    }
  }

  // ================= SAVE =================
  handleSave = (form: any) => {
    const payload = {
      answrSeq:    this.isEditMode ? form.ansSeq : null,  // ✅ send seq on update, null on create
      qstSeq:      this.qstSeq,
      answrStr:    form.ansStr,
      answrScore:  Number(form.ansScore),
      answrStsKey: form.status,
      delFlg:      false
    };

    return this.setup.saveAnswer(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant(this.isEditMode ? 'QUESTIONNAIRE.UPDATED' : 'QUESTIONNAIRE.CREATED'));
        this.showPopup = false;
        this.loadData();
      },
      error: () => this.toastr.error(this.translate.instant('QUESTIONNAIRE.SAVE_FAILED'))
    });
  };

  // ================= DELETE =================
  deleteAnswer(row: any): void {
    const payload = {
      answrSeq:    row.ansSeq,         // ✅ existing seq
      qstSeq:      this.qstSeq,
      answrStr:    row.ansStr,
      answrScore:  row.ansScore,
      answrStsKey: row.status,
      delFlg:      true                // ✅ soft delete
    };

    this.setup.saveAnswer(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('QUESTIONNAIRE.DELETED'));
        this.loadData();
      },
      error: () => this.toastr.error(this.translate.instant('QUESTIONNAIRE.DELETE_FAILED'))
    });
  }

  // ================= ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.icons.edit,
      tooltip: 'COMMON.EDIT',
      class: 'action-edit',
      onClick: (row) => this.openPopup(row),
    },
    {
      icon: this.icons.delete,                    // ✅ ADD delete action
      tooltip: 'COMMON.DELETE',
      class: 'action-delete',
      onClick: (row) => this.openDeletePopup(row),
      hide: () => this.typeCategory === 'radio',   // Yes/No pair is fixed — can't delete down below 2
    }
  ];

  // ================= DELETE CONFIRM =================
  openDeletePopup(row: any) {
    this.selectedRow = row;
    this.deleteMessage = this.translate.instant('QUESTIONNAIRE.ANSWER_SETUP.DELETE_CONFIRM', { answer: row.ansStr });
    this.showDeletePopup = true;
  }

  confirmDelete(): void {
    if (!this.selectedRow) return;
    this.deleteAnswer(this.selectedRow);
    this.selectedRow = null;
  }
}